import { useLatest } from '@solinkcorp/solink-components';
import { type CircularLog } from 'components/VideoPlayer/controller/CircularLog';
import {
  type PlaybackController,
  type PlayerStatus,
} from 'components/VideoPlayer/controller/PlaybackController';
import { HighResTimestamp } from 'components/VideoPlayer/utils/HighResTimestamp';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useControllerState } from 'solox';
import { Listener } from 'src/core/utils/Listener';
import { getTotalBufferedSeconds, playerTimeToWallClockTime } from 'src/core/video/VideoUtil';
import { useVideoFPS } from './useVideoFPS';

export interface UpdatePlayerStatusCB {
  (params: Partial<Omit<PlayerStatus, 'timestamp'>>): void;
}

/**
 * This handles common things that all players do.
 *
 * This will register listeners for "playing", "paused", "waiting", etc... and
 * update the playback controller as required.  This will also keep track of whether
 * or not the current player needs to be restarted.
 *
 * This returns an `updatePlayerStatus()` that can be called to update
 * the current player status.  This also automatically updates the player status
 * for common events like 'playing' and 'waiting'.
 *
 * To use, pass the returned `setVideoRef` to your video element.
 *
 * Returned values from this function will never change (provided that the
 * playbackController and playerId don't change, which obviously they should not).
 *
 * If playing archive video, a `ttf` should be passed in to do time translation
 * between the video clock and the wall clock time.
 */
export function usePlayerCommon(options: {
  playbackController: PlaybackController;
  playerId: string;
  type: 'Live' | 'Archived';
  playerInfo: string;
  playerPropsSetVideoRef?: (el: HTMLVideoElement | null) => void;
  ttf?: ((playerTime: number) => number) | undefined;
}): {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  setVideoRef: (el: HTMLVideoElement | null) => void;
  updatePlayerStatus: UpdatePlayerStatusCB;
  playerLog: CircularLog;
  /** If this value changes, the player should restart itself. */
  restartRequiredCount: number;
} {
  const { playbackController, playerId, ttf } = options;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const listenerRef = useRef(new Listener());

  const playerLog = playbackController.getPlayerLog(playerId);

  const updatePlayerStatus = useUpdatePlayerStatus(
    playbackController,
    playerId,
    options.type,
    options.playerInfo,
    videoRef,
    ttf,
  );

  // Do we need to restart this player?
  const restartRequiredCount = useRestartRequired(playbackController, updatePlayerStatus, playerId);

  const latestPlayerPropsSetVideoRef = useLatest(options.playerPropsSetVideoRef);

  const setVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      latestPlayerPropsSetVideoRef.current?.(el);

      const listener = listenerRef.current;
      if (videoRef.current) {
        listener.stopListeningTo(videoRef.current);
      }

      videoRef.current = el;

      if (el) {
        const handleCanPlay = () => {
          // We end up here after a `waiting` event resolves - when video loads.
          // Chrome will fire a "playing" event when the waiting condition resolves, but
          // some browsers (e.g. Safari) will not.
          updatePlayerStatus({ state: el.paused ? 'paused' : 'playing' });
        };

        let lastTimeUpdate = Date.now();
        const handleTimeUpdate = () => {
          const playbackRate = el.playbackRate;

          // In case the playback rate is less than one, we'd want to update the time relative to the playback rate
          // i.e. when playing at 4X, we'd want to update every 1/4 second
          //
          // However, the frequency of the update can vary from 4Hz to 66Hz depends on the system load
          // ref: https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/timeupdate_event
          //
          const sinceLastUpdate = Date.now() - lastTimeUpdate;
          if (sinceLastUpdate < 1000 / playbackRate) {
            return;
          }
          lastTimeUpdate = Date.now();

          updatePlayerStatus({});
        };

        listener.listenTo(el, 'timeupdate', handleTimeUpdate);
        listener.listenTo(el, 'playing', () => updatePlayerStatus({ state: 'playing' }));
        listener.listenTo(el, 'waiting', () => updatePlayerStatus({ state: 'waiting' }));
        listener.listenTo(el, 'pause', () => updatePlayerStatus({ state: 'paused' }));
        listener.listenTo(el, 'ended', () => updatePlayerStatus({ state: 'ended' }));
        listener.listenTo(el, 'canplay', handleCanPlay);
        listener.listenTo(el, 'canplaythrough', handleCanPlay);
        listener.listenTo(el, 'error', () => {
          const error = videoRef.current?.error?.message || 'Error loading video';
          updatePlayerStatus({ state: 'error', error: new Error(error) });
        });
      }
    },
    [latestPlayerPropsSetVideoRef, updatePlayerStatus],
  );

  // On dismount, clean up all listeners.
  useEffect(() => {
    const listener = listenerRef.current;
    return () => listener.stopListening();
  }, []);

  return {
    updatePlayerStatus,
    setVideoRef,
    videoRef,
    playerLog,
    restartRequiredCount,
  };
}

/**
 * Returns an `updatePlayerStatus()` function that automatically fills in various
 * things like the current frames-per-second, or the current player wall-clock
 * time.
 */
function useUpdatePlayerStatus(
  playbackController: PlaybackController,
  playerId: string,
  type: 'Live' | 'Archived',
  playerInfo: string,
  videoRef: React.MutableRefObject<HTMLVideoElement | null>,
  ttf: ((playerTime: number) => number) | undefined,
) {
  // Are we still mounted?
  const mounted = useRef(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const playerTypeRef = useLatest(type);
  const playerInfoRef = useLatest(playerInfo);
  const ttfRef = useLatest(ttf);

  const fpsObserverRef = useVideoFPS(videoRef);

  const latestLoadedBuffers = useRef<{ start: number; end: number }[] | undefined>();
  const lastState = useRef<PlayerStatus['state']>('loading');
  const updatePlayerStatus = useCallback<UpdatePlayerStatusCB>(
    (params) => {
      if (!mounted.current) {
        return;
      }

      lastState.current = params.state || lastState.current;

      // Work out which buffers are loaded
      // Work out the current wall-clock time for the player.
      const wallclockTime = getWallClockTime(playbackController, videoRef.current, ttfRef.current);

      const [totalBufferedSeconds, loadedBuffers] = getLoadedBuffers(
        videoRef.current,
        ttfRef.current,
      );
      if (!buffersEqual(latestLoadedBuffers.current, loadedBuffers)) {
        latestLoadedBuffers.current = loadedBuffers;
      }

      playbackController.updatePlayerStatus(playerId, {
        // Using performance.now() here to get a high-resolution timestamp unimpacted to user clock adjustments, clock skew, etc.
        // https://developer.mozilla.org/en-US/docs/Web/API/Performance/now
        timestamp: new HighResTimestamp(),
        type: playerTypeRef.current,
        wallclockTime,
        playerInfo: playerInfoRef.current,
        frameRate: fpsObserverRef.current?.fps,
        state: lastState.current,
        buffered: latestLoadedBuffers.current,
        totalBufferedSeconds,
        ...params,
      });
    },
    [playbackController, videoRef, ttfRef, playerId, playerTypeRef, playerInfoRef, fpsObserverRef],
  );
  return updatePlayerStatus;
}

/**
 * Return an array of `{start, end}` object, containing wall-clock time for loaded buffers.
 *
 * The ranges come from a TimeRanges object which, according to the HTML spec,
 * are "ordered, don't overlap, and don't touch (adjacent ranges are folded
 * into one bigger range). A range can be empty (referencing just a single
 * moment in time).
 *
 * @returns a [totalBufferedVideoSeconds, buffers] tuple, where `buffers` is only
 *   defined if the `ttf` function was passed in.
 */
function getLoadedBuffers(
  videoElement: HTMLVideoElement | undefined | null,
  ttf: ((playerTime: number) => number) | undefined,
): [number | undefined, { start: number; end: number }[] | undefined] {
  let result: { start: number; end: number }[] | undefined;
  let totalBufferedSeconds: number | undefined;

  const buffered = videoElement?.buffered;
  if (buffered) {
    if (ttf) {
      result = [];
      for (let i = 0; i < buffered.length; i++) {
        result.push({
          start: playerTimeToWallClockTime(ttf, buffered.start(i)),
          end: playerTimeToWallClockTime(ttf, buffered.end(i)),
        });
      }
    }

    totalBufferedSeconds = getTotalBufferedSeconds(buffered);
  }

  return [totalBufferedSeconds, result];
}

/**
 * Returns true if the two arrays of buffers have the same start and end times.
 */
function buffersEqual(
  a: { start: number; end: number }[] | undefined,
  b: { start: number; end: number }[] | undefined,
) {
  if (a === undefined || b === undefined) {
    return a === b;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (a[i].start !== b[i].start || a[i].end !== b[i].end) {
      return false;
    }
  }

  return true;
}

function getWallClockTime(
  playbackController: PlaybackController,
  videoElement: HTMLVideoElement | null,
  ttf: ((playerTime: number) => number) | undefined,
) {
  const isPlayingLive = playbackController.isPlayingLive();
  if (isPlayingLive) {
    return undefined;
  }

  const currentTime = videoElement?.currentTime;

  return ttf && currentTime ? playerTimeToWallClockTime(ttf, currentTime) : undefined;
}

/**
 * Returns a counter that increments every time a player needs to restart.
 */
function useRestartRequired(
  playbackController: PlaybackController,
  updatePlayerStatus: UpdatePlayerStatusCB,
  playerId: string,
): number {
  const [restartRequiredCount, setRestartRequiredCount] = useState(0);

  const restartRequired =
    useControllerState(
      playbackController.state,
      (state) => state.byPlayerId[playerId]?.state?.restartRequired,
    ) || false;

  useEffect(() => {
    if (restartRequired) {
      setRestartRequiredCount((v) => {
        if (v === Number.MAX_SAFE_INTEGER) {
          v = 0;
        } else {
          v++;
        }

        return v;
      });
      updatePlayerStatus({ restartAcknowledged: true });
    }
  }, [updatePlayerStatus, restartRequired]);

  return restartRequiredCount;
}
